import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from './useAppDispatch';
import { usePortalName } from './usePortalName';
import * as passengerSlice from '../data/passenger.slice';
import { isInIframe, isInPopup } from '../services/authService';
import {
  createAwsConnectWorkspaceBridge,
  shouldEnableAwsConnectBridge,
  type AwsConnectIncomingSearchContext,
} from '../services/awsConnect/awsConnectWorkspaceBridge';

const PASSENGER_SEARCH_ROUTE = '/passengers';

/**
 * True when this document should talk to the Amazon Connect workspace.
 *
 * Access to Connect is the permission: only an agent who can open the workspace can put this app
 * in its iframe, so no feature flag is checked here. Excluded are
 *
 * - the plain browser, where there is no workspace to talk to,
 * - the login popup, which is a top-level window of this app and must not open a second SDK
 *   connection while the iframe already has one,
 * - the client portals, where a lookup across clients must never happen.
 */
export function isAwsConnectWorkspaceContext(
  portalName?: string | null,
): boolean {
  return shouldEnableAwsConnectBridge({
    insideIframe: isInIframe() && !isInPopup(),
    awsConnectEnabled: !portalName,
  });
}

/**
 * Connects the app to the Amazon Connect agent workspace and turns an incoming call into a
 * passenger search.
 *
 * On a call the workspace hands over the contact; the bridge resolves the caller's number and
 * reports it here. This hook then puts the number into the passenger search and navigates to the
 * search page, where the existing effect fires the request. From there the agent takes over.
 *
 * The bridge also picks up a call that was already ringing when it started: the SDK's `onCreate`
 * lifecycle event carries the current contact, so a slow login does not lose the number.
 *
 * Outside the workspace the bridge stays inert - `start()` returns immediately and no SDK module
 * is initialized - so a normal browser session is unaffected.
 */
export function useAwsConnectWorkspace(isLoggedIn: boolean): void {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const portalName = usePortalName();
  const enabled = isLoggedIn && isAwsConnectWorkspaceContext(portalName);

  // The handler is read through a ref so the effect depends on `enabled` alone. Re-running it would
  // tear down a live SDK connection just because a new navigate function was handed to us.
  const handleIncomingRef = useRef<
    (context: AwsConnectIncomingSearchContext) => void
  >(() => undefined);

  handleIncomingRef.current = ({ contact }) => {
    const digits = (contact.initialCustomerPhoneNumber ?? '').replace(
      /\D/g,
      '',
    );
    if (!digits) {
      // A chat or task contact, or a voice contact whose number the workspace withholds. Nothing
      // to search for, so the agent keeps whatever is on screen.
      return;
    }

    dispatch(passengerSlice.setPassengerSearchByPhone(digits));
    navigate(PASSENGER_SEARCH_ROUTE);
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const bridge = createAwsConnectWorkspaceBridge({
      enabled: true,
      onIncomingSearch: (context) => handleIncomingRef.current(context),
      onError: (error) => {
        // The workspace connection is an add-on: if it fails, the app must stay usable and the
        // agent falls back to typing the number. So this is reported, not thrown.
        console.error('Amazon Connect workspace bridge', error);
      },
    });

    void bridge.start();

    return () => bridge.stop();
  }, [enabled]);
}
